import {
  Badge,
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  InternalApiKey,
  IssuedInternalApiKey,
  internalApiKeysApi,
} from '@texas/api/endpoints/internalApiKeysApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { IssueInternalApiKeyModal } from './IssueInternalApiKeyModal';
import { IssuedInternalApiKeyModal } from './IssuedInternalApiKeyModal';

export function InternalApiKeys() {
  const { t } = useTranslation();
  const toast = useToast();
  const createDisclosure = useDisclosure();
  const issuedDisclosure = useDisclosure();
  const [issuedKey, setIssuedKey] = useState<IssuedInternalApiKey | null>(null);

  const {
    data: apiKeys,
    refetch,
    loading,
    error,
  } = useApiResource(internalApiKeysApi.getAll);
  const {
    request: revoke,
    loading: revoking,
    error: revokeError,
  } = useApiRequest(internalApiKeysApi.revoke);
  const {
    request: reactivate,
    loading: reactivating,
    error: reactivateError,
  } = useApiRequest(internalApiKeysApi.reactivate);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const setRevoked = async (apiKey: InternalApiKey) => {
    const response = apiKey.revokedAt
      ? await reactivate(apiKey.id)
      : await revoke(apiKey.id);

    if (!response.hasError) {
      toast({
        title: apiKey.revokedAt
          ? t('internalApiKeys.reactivated')
          : t('internalApiKeys.revoked'),
        status: 'success',
        isClosable: true,
      });
      refetch();
    }
  };

  const onIssued = (apiKey: IssuedInternalApiKey) => {
    setIssuedKey(apiKey);
    createDisclosure.onClose();
    issuedDisclosure.onOpen();
    refetch();
  };

  return (
    <ConfigurationPage
      title={t('internalApiKeys.title')}
      description={t('internalApiKeys.description')}
      action={
        <Button
          variant="entityDirectoryPrimary"
          onClick={createDisclosure.onOpen}
        >
          {t('internalApiKeys.generate')}
        </Button>
      }
    >
      <VStack spacing={4} align="stretch">
        <ErrorDetails error={error ?? revokeError ?? reactivateError} />

        <Box overflowX="auto">
          <Table variant="entity-directory">
            <Thead>
              <Tr>
                <Th>{t('general.name')}</Th>
                <Th>{t('internalApiKeys.issuedBy')}</Th>
                <Th>{t('internalApiKeys.createdAt')}</Th>
                <Th>{t('general.status')}</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {!loading && apiKeys?.length === 0 && (
                <Tr>
                  <Td colSpan={5}>{t('internalApiKeys.empty')}</Td>
                </Tr>
              )}
              {apiKeys?.map((apiKey) => (
                <Tr key={apiKey.id}>
                  <Td>{apiKey.name}</Td>
                  <Td>{apiKey.issuedByUserName}</Td>
                  <Td>{new Date(apiKey.createdAt).toLocaleString()}</Td>
                  <Td>
                    <Badge colorScheme={apiKey.revokedAt ? 'red' : 'green'}>
                      {apiKey.revokedAt
                        ? t('internalApiKeys.revokedStatus')
                        : t('internalApiKeys.active')}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <Menu isLazy={true} variant="entityDirectory">
                      <MenuButton
                        aria-label={t('general.more')}
                        as={Button}
                        variant="entityDirectoryMenu"
                        isDisabled={revoking || reactivating}
                      >
                        <Icons.DotsHorizontal />
                      </MenuButton>
                      <Portal>
                        <MenuList>
                          <MenuItem onClick={() => void setRevoked(apiKey)}>
                            {apiKey.revokedAt
                              ? t('internalApiKeys.reactivate')
                              : t('internalApiKeys.revoke')}
                          </MenuItem>
                        </MenuList>
                      </Portal>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>

      <IssueInternalApiKeyModal
        isOpen={createDisclosure.isOpen}
        onClose={createDisclosure.onClose}
        onIssued={onIssued}
      />

      {issuedKey && (
        <IssuedInternalApiKeyModal
          apiKey={issuedKey}
          isOpen={issuedDisclosure.isOpen}
          onClose={issuedDisclosure.onClose}
        />
      )}
    </ConfigurationPage>
  );
}
